import CM                                from '@emersya/mpa-components-manager-lib';

export interface IPlannerComponentConfiguration extends CM.Component.IConfiguration {
	readonly groundMaterialTree : string | CM.PropertyOperation.TDescriptor;
	readonly doNotDisplayFurniturePriceIfItsZero : boolean;
	readonly size : ViewerComponent.ISize;
	readonly naturalSize : ViewerComponent.ISize;
	readonly applyDefaultSize : boolean;
};

export class PlannerComponent extends CM.Component<
	PlannerComponent,
	IPlannerComponentConfiguration
>
{
	static count = 0;
	static self : PlannerComponent;

	static override initialize() {
		CM.Component.initialize.call(this);
		document.addEventListener('mousemove', this.onMouseMove.bind(this));
		document.addEventListener('mouseup', this.onMouseUp.bind(this));
		// this.eventsTypes.push(...Emersya.Viewer.Planner.eventsTypes.filter(x => [
		// 	'message',
		// 	'selection',
		// 	'productAdded',
		// 	'productRemoved'
		// ].includes(x.type)));
		this.declareEventType('planner-loaded');
	};

	private static async onMouseMove(ev : MouseEvent) {
		const self = this.self;
		if (!self) {
			return;
		}
		if (!self.inDragg) {
			return;
		}
		self.cursorUpdatePositionFromMouseEvent(ev);
		const t = ev.target as HTMLElement | null;
		// if cursor is over the viewer
		if (t && (t.dataset.componentType === ViewerComponent.type)) {
			await self.instantiateProduct();
		}
	};

	private static async onMouseUp() {
		if (!this.self) {
			return;
		}
		if (this.self.inDragg) {
			// User released outside viewer before inserting in 3D so cancel.
			await this.self.dragStop();
		} else if (this.self.inDraggInViewer) {
			// User released outside viewer after inserting in 3D so cancel with a delete.
			await this.self.inDraggInViewer.delete();
			this.self.inDraggInViewer = null;
		}
	};

	override component_parent : ViewerComponent;
	msTree : string;
	msSetting : string = '';
	naturalSize : ViewerComponent.ISize;
	size : ViewerComponent.ISize;

	async updateGroundUV() {
		const v = app.viewer;
		const materials = (
			await v.getCurrentMaterials()
		).materialSettingsConfiguration;
		if (!materials) {
			this.component_throwLibraryError('');
		}
		const material = materials.find(x => x.materialTree === this.msTree);
		if (!material) {
			this.component_throwLibraryError('');
		}

		await v.transformTexture({
			configurableMaterial : this.msTree,
			materialVariation :    this.msSetting,
			contents :             [
				'albedo',
				'albedo.specular',
				'albedo.specularIntensity',
				'roughness',
				'roughness.sheen',
				'roughness.sheenIntensity',
				'roughness.sheenRoughness',
				'metalness',
				'metalness.anisotropy',
				'metalness.anisotropyIntensity',
				'metalness.anisotropyRoughness',
				'metalness.anisotropyRotation',
				'opacity',
				'normalDeformation',
				'normalDeformationIntensity',
				'normalDeformation2',
				'normalDeformationIntensity2',
				'translucency',
				'emissive',
				'emissiveIntensity',
				'emissive.glowColor',
				'emissive.glowOpacity',
				'emissive.glowSaturation',
				'iridescence',
				'coatingIntensity',
				'coatingThickness',
				'coatingRoughness',
				'occlusionAmbient',
				'occlusionLight'
			],
			transform :            {
				rotation :       0,
				translation :    [ 0, 0, 0 ],
				rotationCenter : [ 0, 0, 0 ],
				scaling :        [
					this.size.width,
					this.size.depth,
					this.size.height
				]
			}
		});
	};

	private async setSize(size : Partial<ViewerComponent.ISize>) {
		if (size.depth) {
			this.size.depth = size.depth;
		}
		if (size.width) {
			this.size.width = size.width;
		}
		if (size.height) {
			this.size.height = size.height;
		}
		await app.viewer.resizePlannerSpace({
			length : this.size.width,
			width :  this.size.depth,
			height : this.size.height
		});
	};


	async updateSize(
		what : 'width' | 'depth' | 'height',
		value : number
	) : Promise<void>;
	async updateSize(
		size : Partial<ViewerComponent.ISize>
	) : Promise<void>;
	async updateSize(
		...args : [
				'width' | 'depth' | 'height',
			number
		] | [
			Partial<ViewerComponent.ISize>
		]
	) : Promise<void> {
		const v = app.viewer;
		await v.freezeRendering({ keepCount : true });
		if (args.length === 1) {
			await this.setSize(args[ 0 ]);
		} else {
			await this.setSize({ [ args[ 0 ] ] : args[ 1 ] });
		}
		await this.updateGroundUV();
		await v.unfreezeRendering({ keepCount : true });
	};

	async updateMs(msSetting : string) {
		this.msSetting = msSetting;
		const v = app.viewer;
		await v.freezeRendering({ keepCount : true });
		await this.updateGroundUV();
		await v.setMaterials({
			nodeLocalIds : [ 1 ],
			materials :    [
				{
					configurableMaterial : this.msTree,
					materialVariation :    this.msSetting
				}
			]
		});
		await v.unfreezeRendering({ keepCount : true });
	};

	async onPlannerLoaded() {
		// const extendedProducts = (
		// 	this.planner.categories.map(x => x.products.filter(y => y.extension)).filter(x => x.length).flat()
		// );
		// for (const extendedProduct of extendedProducts) {
		// 	const extension = <NonNullable<typeof extendedProduct.extension>> extendedProduct.extension;
		// 	const id = 'plannerProduct.' + extendedProduct.getId();
		// 	if (extension.name) {
		// 		API.translationsManager.addObject(id + '.name', extension.name);
		// 	}
		// 	if (extension.description) {
		// 		API.translationsManager.addObject(id + '.description', extension.description);
		// 	}
		// }
		await this.dispatchEvent('planner-loaded', {});
	};

	async directInstanciation(product : ViewerComponent.Product) {
		await product.instantiate({appendType : 'drop', rootLocalId : 1, basketPart : [], basketUseDynamicTitleBinding : false, metadata : null});
		// await product.instantiate(1, false, null);
	};

	async dragStart( //eslint-disable-line require-await
		ev : MouseEvent,
		product : ViewerComponent.Product
	)
	{
		if (this.inDragg) {
			this.component_throwLibraryError('');
		}
		if (!ev.target || !(ev.target instanceof HTMLElement)) {
			this.component_throwLibraryError('');
		}
		document.body.classList.add('unselectable');
		app.viewer.disableIframeInteractions();
		this.inDragg = {
			product,
			source :  ev.target,
			iconDOM : product.iconDOM.cloneNode(true) as HTMLImageElement
		};
		this.inDragg.iconDOM.classList.add('plannerInDraggComponentIcon');
		this.component_parent.application.component_DOM.classList.add('planner-inDragg');
		this.cursorUpdatePositionFromMouseEvent(ev);
		document.body.appendChild(this.inDragg.iconDOM);
	};

	async dragStop() { //eslint-disable-line
		if (!this.inDragg) {
			this.component_throwLibraryError('');
		}
		document.body.classList.remove('unselectable');
		this.component_parent.enableIframeInteractions();
		this.component_parent.application.component_DOM.classList.remove('planner-inDragg');
		this.inDragg.iconDOM.remove();
		this.inDragg = null;
	};

	private inDragg : {
		source : HTMLElement;
		product : ViewerComponent.Product;
		iconDOM : HTMLImageElement;
	} | null = null;
	inDraggInViewer : ViewerComponent.ProductInstance | null = null;

	private async onProductNodeInstanceDraggingStopped() { //eslint-disable-line
		if (this.inDraggInViewer) {
			this.inDraggInViewer = null;
		}
	};

	/**
	 * this.inDragg should be manually checked before call
	 **/
	private cursorUpdatePositionFromMouseEvent(ev : MouseEvent) {
		const inDrag = this.inDragg as NonNullable<typeof this.inDragg>;
		const i = inDrag.iconDOM;
		i.style.left = (ev.x - (i.naturalWidth / 2)) + 'px';
		i.style.top = (ev.y - (i.naturalHeight / 2)) + 'px';
	};

	/**
	 * this.inDragg should be manually checked before call
	 **/
	private async instantiateProduct() {
		const inDrag = this.inDragg as NonNullable<typeof this.inDragg>;
		const product = inDrag.product;
		await this.dragStop();
		this.inDraggInViewer = await product.instantiate({rootLocalId : 1, appendType : 'drop', basketPart : [], basketUseDynamicTitleBinding : false, metadata : { isUserInserted : true }});
	};

	constructor(
		descriptor : ConstructorParameters<typeof CM.Component>[0],
		configuration : IPlannerComponentConfiguration
	)
	{
		super(descriptor, configuration);
		if (PlannerComponent.count !== 0) {
			this.component_throwUserError('Only one planner component is allowed!');
		}
		PlannerComponent.self = this;
		this.component_parent = descriptor.parent as ViewerComponent;
		if (!(this.component_parent instanceof ViewerComponent)) {
			this.component_throwUserError(
				'Planner component should be a direct child of a viewer component!',
				{ parent : this.component_parent }
			);
		}
		const appComponent = this.component_getRoot();
		if (!(appComponent instanceof MPAEngineApplicationComponent)) {
			this.component_throwUserError(
				'Root component must be an instance of an application component!',
				{ root : appComponent }
			);
		}
		if (!configuration.size) {
			this.component_throwUserError(
				'Planner configuration should includes size object!',
				{ configuration }
			);
		}
		this.size = { ...configuration.size };
		this.naturalSize = { ...configuration.naturalSize };
		this.msTree = (
			(typeof configuration.groundMaterialTree === 'string') ? configuration.groundMaterialTree :
				''
		);
		this.component_parent.addEventListener('message', this.onProductNodeInstanceDraggingStopped.bind(this), {
			listener : this,
			subType :  'onProductNodeInstanceDraggingStopped'
		});
		PlannerComponent.count++;
	};
};
export namespace NS {
	export const Component = PlannerComponent;
	export type Component = PlannerComponent;
	export type Configuration = IPlannerComponentConfiguration;
}
;
import { MPAEngineApplicationComponent } from '../../../mpa-engine/Application/MPAEngineApplication';
import ViewerComponent                   from '../../Viewer/Viewer';

CM.declare({
	id :   'viewer-planner',
	type : PlannerComponent
});
